import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/AuthContext";

/* Sessie afronden-flow (spec #5) — alle velden optioneel.
   Bij al afgeronde sessies werkt dezelfde modal als "feedback toevoegen/bewerken". */
export default function PTComplete({ booking, onClose, onDone }) {
  const isCompleted = booking.status === "completed";
  const [f, setF] = useState({
    went_well: booking.feedback?.went_well || "",
    focus: booking.feedback?.focus || "",
    next_step: booking.feedback?.next_step || "",
    trainer_note: booking.feedback?.trainer_note || "",
    private_note: "",
    milestones: "",
    feedback_visible: booking.feedback?.visible !== false,
    send_mail: false,
    payment_on: false, payment_kind: "single", payment_amount: "",
    next_on: false, next_date: "", next_time: "",
  });
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { api.get("/pt/settings").then(({ data }) => setPackages(data.packages || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (f.payment_on && !f.payment_amount) {
      const p = packages.find((x) => x.key === f.payment_kind);
      if (p) set("payment_amount", p.price);
    }
  }, [f.payment_kind, f.payment_on, packages]); // eslint-disable-line

  const submit = async () => {
    setBusy(true);
    try {
      if (isCompleted) {
        await api.post(`/pt/admin/bookings/${booking.id}/feedback`, {
          went_well: f.went_well, focus: f.focus, next_step: f.next_step, trainer_note: f.trainer_note,
          visible: f.feedback_visible, send_mail: f.send_mail,
        });
        toast.success("Feedback opgeslagen");
      } else {
        const payload = {
          went_well: f.went_well, focus: f.focus, next_step: f.next_step, trainer_note: f.trainer_note,
          private_note: f.private_note, feedback_visible: f.feedback_visible,
          milestones: f.milestones.split("\n").map((m) => m.trim()).filter(Boolean),
        };
        if (f.payment_on) payload.payment = { kind: f.payment_kind, amount: f.payment_amount || undefined };
        if (f.next_on && f.next_date && f.next_time) payload.next_session = { date: f.next_date, time: f.next_time };
        payload.origin_url = window.location.origin;
        const { data } = await api.post(`/pt/admin/bookings/${booking.id}/complete`, payload);
        if (data.payment_error) toast.warning(`Sessie afgerond, maar betaalverzoek mislukt: ${data.payment_error}`);
        else if (data.payment_request) toast.success("Sessie afgerond + betaalverzoek klaargezet");
        else toast.success("Sessie afgerond");
        if (data.next_session_error) toast.warning(data.next_session_error);
        else if (data.next_session) toast.success(`Vervolgsessie gepland: ${data.next_session.date} ${data.next_session.time}`);
      }
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Afronden mislukt");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pt-complete-modal">
        <div className="flex items-start justify-between mb-1">
          <div className="chip text-[#A3A3A3]">// {isCompleted ? "FEEDBACK BEWERKEN" : "SESSIE AFRONDEN"} — {booking.date} · {booking.time}</div>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white" data-testid="pt-complete-close"><X size={16}/></button>
        </div>
        <div className="font-display uppercase text-2xl mb-5">{booking.first_name} {booking.last_name}</div>

        <div className="space-y-4">
          <div><label className="chip text-[#A3A3A3]">Wat ging goed?</label><textarea rows={2} value={f.went_well} onChange={(e) => set("went_well", e.target.value)} className="editorial-input dark w-full" data-testid="pt-complete-went-well" placeholder="Bv. Je jab-cross combinatie werd steeds scherper…" /></div>
          <div><label className="chip text-[#A3A3A3]">Focuspunt volgende training</label><textarea rows={2} value={f.focus} onChange={(e) => set("focus", e.target.value)} className="editorial-input dark w-full" data-testid="pt-complete-focus" placeholder="Bv. Blijf na je combinatie beter uit de lijn bewegen…" /></div>
          <div><label className="chip text-[#A3A3A3]">Volgende stap</label><textarea rows={2} value={f.next_step} onChange={(e) => set("next_step", e.target.value)} className="editorial-input dark w-full" data-testid="pt-complete-next-step" placeholder="Bv. Defence + counter combinations…" /></div>
          <div><label className="chip text-[#A3A3A3]">Trainer note (deelbaar met klant)</label><textarea rows={2} value={f.trainer_note} onChange={(e) => set("trainer_note", e.target.value)} className="editorial-input dark w-full" data-testid="pt-complete-note" /></div>
          {!isCompleted && (
            <div><label className="chip text-[#A3A3A3]">Interne notitie (privé — klant ziet dit niet)</label><textarea rows={2} value={f.private_note} onChange={(e) => set("private_note", e.target.value)} className="editorial-input dark w-full" data-testid="pt-complete-private" /></div>
          )}
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={f.feedback_visible} onChange={(e) => set("feedback_visible", e.target.checked)} className="w-4 h-4 accent-[#D4A93A]" data-testid="pt-complete-visible" />
            <span className="chip text-[#DDD6CB]">Feedback zichtbaar voor klant</span>
          </label>
          {isCompleted && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={f.send_mail} onChange={(e) => set("send_mail", e.target.checked)} className="w-4 h-4 accent-[#D4A93A]" data-testid="pt-complete-sendmail" />
              <span className="chip text-[#DDD6CB]">Stuur klant een e-mail over deze feedback</span>
            </label>
          )}

          {!isCompleted && (
            <>
              <div><label className="chip text-[#A3A3A3]">Milestones (één per regel, optioneel)</label><textarea rows={2} value={f.milestones} onChange={(e) => set("milestones", e.target.value)} className="editorial-input dark w-full" data-testid="pt-complete-milestones" placeholder="Eerste volle ronde pads zonder pauze" /></div>

              <div className="border border-[#F5F3F0]/15 p-4">
                <label className="flex items-center gap-3 cursor-pointer mb-3">
                  <input type="checkbox" checked={f.payment_on} onChange={(e) => set("payment_on", e.target.checked)} className="w-4 h-4 accent-[#D4A93A]" data-testid="pt-complete-payment-on" />
                  <span className="chip text-[#DDD6CB]">Betaalverzoek klaarzetten</span>
                </label>
                {f.payment_on && (
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="chip text-[#A3A3A3]">Pakket</label>
                      <select value={f.payment_kind} onChange={(e) => set("payment_kind", e.target.value)} className="editorial-input dark" data-testid="pt-complete-payment-kind">
                        {packages.map((p) => <option key={p.key} value={p.key} className="bg-[#0A0A0A]">{p.name} ({p.sessions} sessies)</option>)}
                      </select>
                    </div>
                    <div><label className="chip text-[#A3A3A3]">Bedrag (€)</label><input type="number" min="0" step="0.01" value={f.payment_amount} onChange={(e) => set("payment_amount", e.target.value)} className="editorial-input dark" data-testid="pt-complete-payment-amount" /></div>
                  </div>
                )}
                {f.payment_on && <p className="chip text-[#4A4A4A] mt-2">Wordt klaargezet — je verstuurt de link zelf via Betalingen.</p>}
              </div>

              <div className="border border-[#F5F3F0]/15 p-4">
                <label className="flex items-center gap-3 cursor-pointer mb-3">
                  <input type="checkbox" checked={f.next_on} onChange={(e) => set("next_on", e.target.checked)} className="w-4 h-4 accent-[#D4A93A]" data-testid="pt-complete-next-on" />
                  <span className="chip text-[#DDD6CB]">Direct nieuwe sessie plannen</span>
                </label>
                {f.next_on && (
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="chip text-[#A3A3A3]">Datum</label><input type="date" value={f.next_date} onChange={(e) => set("next_date", e.target.value)} className="editorial-input dark" data-testid="pt-complete-next-date" /></div>
                    <div><label className="chip text-[#A3A3A3]">Startuur</label><input type="time" step="3600" value={f.next_time} onChange={(e) => set("next_time", e.target.value)} className="editorial-input dark" data-testid="pt-complete-next-time" /></div>
                  </div>
                )}
                {f.next_on && <p className="chip text-[#4A4A4A] mt-2">De sessie wordt meteen bevestigd en de klant krijgt een mail.</p>}
              </div>
            </>
          )}

          <button onClick={submit} disabled={busy} className="w-full py-3.5 chip" style={{ background: "var(--joh-accent)", color: "#0A0A0A" }} data-testid="pt-complete-submit">
            {busy ? "…" : isCompleted ? "FEEDBACK OPSLAAN" : "SESSIE AFRONDEN"}
          </button>
        </div>
      </div>
    </div>
  );
}
